/**
 * CLIENTES PAGE - Client Management
 * Gestión de clientes del laboratorio (áreas hospitalarias, empresas, particulares)
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Building2,
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  X,
} from "lucide-react";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useCliente,
  useActivateCliente,
  useDeactivateCliente,
  useDeleteCliente,
  type ClienteListItem,
} from "@/hooks/use-clientes";
import { getClientes, TIPOS_CLIENTE } from "@/hooks/use-clientes";

import { ClienteFormDialog } from "./components/cliente-form-dialog";

const TIPO_BADGE_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  AREA_HOSPITALARIA: "default",
  EMPRESA: "secondary",
  PARTICULAR: "outline",
};

export default function ClientesPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedClienteId, setSelectedClienteId] = useState<number | null>(null);
  const [selectedClienteForAction, setSelectedClienteForAction] = useState<ClienteListItem | null>(
    null
  );
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Fetch full cliente data for editing
  const { data: selectedCliente } = useCliente(selectedClienteId || 0, {
    enabled: !!selectedClienteId,
  });
  const isFormReady = selectedClienteId === null || !!selectedCliente;

  // Mutations
  const activateMutation = useActivateCliente({
    onSuccess: () => {
      toast.success(t("clientes.toast.activated", { name: selectedClienteForAction?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivateCliente({
    onSuccess: () => {
      toast.warning(t("clientes.toast.deactivated", { name: selectedClienteForAction?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteCliente({
    onSuccess: () => {
      toast.error(t("clientes.toast.deleted", { name: selectedClienteForAction?.nombre }));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleEdit = useCallback((cliente: ClienteListItem) => {
    setSelectedClienteId(cliente.id);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((cliente: ClienteListItem) => {
    setSelectedClienteForAction(cliente);
    setConfirmAction(cliente.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((cliente: ClienteListItem) => {
    setSelectedClienteForAction(cliente);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedClienteId(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedClienteForAction || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedClienteForAction.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedClienteForAction.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedClienteForAction.id);
    setIsConfirmOpen(false);
    setSelectedClienteForAction(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("clientes.toast.created") : t("clientes.toast.updated"));
    setSelectedClienteId(null);
    refetch();
  };

  // Columns
  const columns: ColumnDef<ClienteListItem>[] = useMemo(
    () => [
      {
        accessorKey: "codigo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("clientes.columns.code")} />
        ),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.codigo}</span>,
      },
      {
        accessorKey: "nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("clientes.columns.name")} />
        ),
        cell: ({ row }) => <span className="font-medium">{row.original.nombre}</span>,
      },
      {
        accessorKey: "tipo",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("clientes.columns.type")} />
        ),
        cell: ({ row }) => (
          <Badge variant={TIPO_BADGE_VARIANT[row.original.tipo] || "outline"}>
            {row.original.tipo_display}
          </Badge>
        ),
      },
      {
        accessorKey: "contacto_nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("clientes.columns.contact")} />
        ),
        cell: ({ row }) => <span className="text-sm">{row.original.contacto_nombre || "-"}</span>,
      },
      {
        accessorKey: "contacto_telefono",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("clientes.columns.phone")} />
        ),
        cell: ({ row }) => <span className="text-sm">{row.original.contacto_telefono || "-"}</span>,
      },
      {
        accessorKey: "is_active",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("clientes.columns.status")} />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const cliente = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(cliente)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(cliente)}>
                  {cliente.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!cliente.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(cliente)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  // Server-side data table
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getClientes,
    queryKey: "clientes",
    storageKey: "clientes_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "is_active", defaultValue: "all", urlParam: "status" },
      { key: "tipo", defaultValue: "all", urlParam: "tipo" },
    ],
  });

  const columnLabels = {
    codigo: t("clientes.columns.code"),
    nombre: t("clientes.columns.name"),
    tipo: t("clientes.columns.type"),
    contacto_nombre: t("clientes.columns.contact"),
    contacto_telefono: t("clientes.columns.phone"),
    is_active: t("clientes.columns.status"),
  };

  // Mobile card
  const renderMobileCard = useCallback(
    (cliente: ClienteListItem) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{cliente.codigo}</span>
            <Badge variant={TIPO_BADGE_VARIANT[cliente.tipo] || "outline"} className="text-xs">
              {cliente.tipo_display}
            </Badge>
            <Badge variant={cliente.is_active ? "default" : "secondary"} className="shrink-0">
              {cliente.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
          </div>
          <p className="font-medium mt-1">{cliente.nombre}</p>
          {cliente.contacto_nombre && (
            <p className="text-sm text-muted-foreground mt-1">{cliente.contacto_nombre}</p>
          )}
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleEdit(cliente)}>
              <Pencil className="h-3 w-3 mr-1" />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleToggleStatus(cliente)}>
                  {cliente.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!cliente.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(cliente)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ),
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("clientes.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("clientes.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreate}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("clientes.newClient")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("clientes.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filters.tipo} onValueChange={(value) => setFilter("tipo", value)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder={t("clientes.filterByType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("clientes.allTypes")}</SelectItem>
                  {TIPOS_CLIENTE.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.is_active}
                onValueChange={(value) => setFilter("is_active", value)}
              >
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder={t("clientes.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("clientes.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>{t("clientes.showingResults", { count: totalCount })}</CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("clientes.noClients")}
            hideOnTablet={["contacto_telefono"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ClienteFormDialog
        open={isFormOpen && isFormReady}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setSelectedClienteId(null);
        }}
        cliente={selectedCliente || null}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("clientes.confirmActivate")
            : confirmAction === "delete"
              ? t("clientes.confirmDelete")
              : t("clientes.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("clientes.confirmActivateDesc", { name: selectedClienteForAction?.nombre })
            : confirmAction === "delete"
              ? t("clientes.confirmDeleteDesc", { name: selectedClienteForAction?.nombre })
              : t("clientes.confirmDeactivateDesc", { name: selectedClienteForAction?.nombre })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
